import { useEffect, useState } from 'react';
import { fetchSupervisorLocationHistory } from '../lib/supervisorLocationApi';

export function useSupervisorLocationHistory(
  feedbackSupervisorUid,
  startDate,
  endDate,
  maxLimit,
  { includeLive = false } = {},
) {
  const [history, setHistory] = useState([]);
  const [liveLocation, setLiveLocation] = useState(null);
  const [liveLocationAt, setLiveLocationAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startMs = startDate?.getTime?.() ?? null;
  const endMs = endDate?.getTime?.() ?? null;

  useEffect(() => {
    if (!feedbackSupervisorUid || startMs == null || endMs == null) {
      setHistory([]);
      setLiveLocation(null);
      setLiveLocationAt(null);
      return undefined;
    }

    // Synchronously clear stale data so the map doesn't briefly show the
    // previous supervisor's trail while the new fetch is in flight.
    setHistory([]);
    setLiveLocation(null);
    setLiveLocationAt(null);

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchSupervisorLocationHistory({
          feedbackSupervisorUid,
          startMs,
          endMs,
          limit: maxLimit,
          includeLive,
        });
        if (cancelled) return;
        setHistory(result.samples ?? []);
        const live = result.liveLocation;
        if (live && typeof live.lat === 'number' && typeof live.lng === 'number') {
          setLiveLocation({ lat: live.lat, lng: live.lng });
          setLiveLocationAt(live.capturedAtMs ?? null);
        } else {
          setLiveLocation(null);
          setLiveLocationAt(null);
        }
      } catch (err) {
        console.error('useSupervisorLocationHistory:', err);
        if (!cancelled) {
          setHistory([]);
          setLiveLocation(null);
          setLiveLocationAt(null);
          setError(err.message || 'Failed to load location history');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [feedbackSupervisorUid, startMs, endMs, maxLimit, includeLive]);

  return { history, liveLocation, liveLocationAt, loading, error };
}
